import { Pressable, Text, ActivityIndicator, type PressableProps } from 'react-native';
import { useColors } from '../lib/colors';

interface ButtonProps extends PressableProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'destructive' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  className?: string;
}

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled,
  className = '',
  ...rest
}: ButtonProps) {
  const c = useColors();

  const baseContainer = 'flex-row items-center justify-center rounded-xl';
  const sizeContainer = {
    sm: 'px-3 py-2',
    md: 'px-5 py-3',
    lg: 'px-6 py-4',
  }[size];

  const variantContainer = {
    primary: 'bg-forest-600 dark:bg-forest-600',
    secondary: 'bg-gray-100 dark:bg-gray-700',
    destructive: 'bg-red-600 dark:bg-red-700',
    ghost: 'bg-transparent',
  }[variant];

  const disabledContainer = disabled || loading ? 'opacity-50' : '';

  const sizeText = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  }[size];

  const variantText = {
    primary: 'text-white font-semibold',
    secondary: 'text-gray-800 dark:text-gray-200 font-semibold',
    destructive: 'text-white font-semibold',
    ghost: 'text-forest-600 dark:text-forest-500 font-semibold',
  }[variant];

  const spinnerColor =
    variant === 'secondary' || variant === 'ghost' ? c.primary : '#ffffff';

  return (
    <Pressable
      className={`${baseContainer} ${sizeContainer} ${variantContainer} ${disabledContainer} ${className}`}
      disabled={disabled || loading}
      {...rest}
    >
      {loading && (
        <ActivityIndicator
          size="small"
          color={spinnerColor}
          className="mr-2"
        />
      )}
      <Text className={`${sizeText} ${variantText}`}>{children}</Text>
    </Pressable>
  );
}
